(() => {
  // The versioned key intentionally leaves the old exclusive-default preference
  // behind so every customer receives the new inclusive default once.
  const STORAGE_KEY = 'dt-vat-display-v2';
  const PRICE_SELECTOR = [
    '.price-item',
    '.cart-item__price-wrapper', '.cart-item__details .product-option',
    '.totals__total-value',
    '.totals__subtotal-value',
    '.quick-order-list .price',
    'quick-order-list .price',
    'quick-order-list .product-option',
    '.quick-order-list .product-option',
    'volume-pricing li span:last-child',
  ].join(',');
  const PRICE_BOUNDARY_SELECTOR = [
    '.cart-item__price-wrapper', '.cart-item__details .product-option',
    '.totals__total-value',
    '.totals__subtotal-value',
    '.quick-order-list .price',
    'quick-order-list .price',
    'quick-order-list .product-option',
    '.quick-order-list .product-option',
    'volume-pricing li',
  ].join(',');
  const PRICE_IDENTITY_ATTRIBUTES = [
    'data-product-id',
    'data-variant-id',
    'data-cart-item-key',
    'data-line-item-key',
  ];
  const POUND_AMOUNT = /£(\s*)(\d[\d,]*(?:\.\d{1,2})?)/g;
  const MONEY_FORMATTER = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  class VatPriceController {
    constructor(root) {
      this.rate = 1 + (Number.parseFloat(root.dataset.vatRate || '20') / 100);
      this.inclusive = this.readPreference() !== 'exclusive';
      this.hasUsedExclusive = !this.inclusive;
      this.textState = new WeakMap();
      this.boundaryState = new WeakMap();
      this.roots = new Set();
      this.rootControls = new WeakMap();
      this.pendingScopes = new Set();
      this.frame = null;

      this.registerRoot(root);
      // Inclusive is the native storefront price; a full pass is needed only
      // when the customer selects the exclusive display.
      if (!this.inclusive) this.processScope(document, true);
      if (this.hasUsedExclusive) this.observePriceChanges();
    }

    registerRoot(root) {
      if (this.roots.has(root)) return;

      const toggle = root.querySelector('[data-dt-vat-toggle]');
      const handleToggle = () => this.setInclusive(!this.inclusive);
      toggle?.addEventListener('click', handleToggle);

      this.roots.add(root);
      this.rootControls.set(root, { toggle, handleToggle });
      this.syncControl(root);
    }

    unregisterRoot(root) {
      const control = this.rootControls.get(root);
      control?.toggle?.removeEventListener('click', control.handleToggle);
      this.rootControls.delete(root);
      this.roots.delete(root);
    }

    isInsideUtilityBar(node) {
      return [...this.roots].some((root) => root === node || root.contains(node));
    }

    readPreference() {
      try {
        const preference = window.localStorage.getItem(STORAGE_KEY);
        return preference === 'inclusive' || preference === 'exclusive' ? preference : 'inclusive';
      } catch (_error) {
        return 'inclusive';
      }
    }

    writePreference() {
      try {
        window.localStorage.setItem(STORAGE_KEY, this.inclusive ? 'inclusive' : 'exclusive');
      } catch (_error) {
        // Price display still works for the current page when storage is unavailable.
      }
    }

    setInclusive(inclusive) {
      this.inclusive = Boolean(inclusive);
      if (!this.inclusive) this.hasUsedExclusive = true;
      this.writePreference();
      this.syncControl();
      this.processScope(document, true);
      if (!this.inclusive && !this.observer) this.observePriceChanges();
    }

    syncControl(onlyRoot = null) {
      const mode = this.inclusive ? 'inclusive' : 'exclusive';
      const roots = onlyRoot ? [onlyRoot] : [...this.roots];

      roots.forEach((root) => {
        root.dataset.vatMode = mode;
        const toggle = this.rootControls.get(root)?.toggle;
        if (!toggle) return;

        toggle.setAttribute('aria-checked', String(this.inclusive));
        toggle.setAttribute(
          'aria-label',
          this.inclusive ? 'Show prices without VAT' : 'Show prices with VAT'
        );
      });
      document.dispatchEvent(new CustomEvent('dt:vat-change'));
    }

    hasPoundAmount(text) {
      POUND_AMOUNT.lastIndex = 0;
      const found = POUND_AMOUNT.test(text);
      POUND_AMOUNT.lastIndex = 0;
      return found;
    }

    transformToExclusivePriceText(text) {
      return text.replace(POUND_AMOUNT, (_match, spacing, numberText) => {
        const value = Number.parseFloat(numberText.replace(/,/g, ''));
        if (!Number.isFinite(value)) return _match;

        const exclusiveValue = Math.round((value / this.rate + Number.EPSILON) * 100) / 100;
        return `£${spacing}${MONEY_FORMATTER.format(exclusiveValue)}`;
      });
    }

    processTextNode(node, force) {
      if (node.parentElement?.closest('[data-dt-vat-ignore]')) return;
      const current = node.nodeValue || '';
      const tracked = this.textState.get(node);

      if (!this.hasPoundAmount(current) && !(tracked && this.hasPoundAmount(tracked.base))) return;

      let base = current;
      if (tracked && tracked.rendered === current) {
        base = tracked.base;
        if (!force) return;
      }

      const rendered = this.inclusive ? base : this.transformToExclusivePriceText(base);
      this.textState.set(node, { base, rendered });

      if (current !== rendered) {
        node.nodeValue = rendered;
      }
    }

    findPriceBoundary(element) {
      if (!(element instanceof Element)) return null;
      return element.closest(PRICE_BOUNDARY_SELECTOR) ||
        (element.matches(PRICE_SELECTOR) ? element : null);
    }

    boundaryIdentity(boundary) {
      const parts = [];
      let element = boundary;
      let depth = 0;

      while (element && element !== document.body && depth < 5) {
        PRICE_IDENTITY_ATTRIBUTES.forEach((attribute) => {
          if (element.hasAttribute(attribute)) {
            parts.push(`${attribute}:${element.getAttribute(attribute)}`);
          }
        });
        element = element.parentElement;
        depth += 1;
      }

      return parts.join('|');
    }

    priceRole(node) {
      const element = node.parentElement?.closest(PRICE_SELECTOR);
      if (!element) return '';

      const attributes = [
      ].filter((attribute) => element.hasAttribute(attribute));

      return [element.tagName, element.className || '', ...attributes].join('|');
    }

    textNodesWithin(root) {
      if (root.nodeType === Node.TEXT_NODE) return [root];
      if (!root.ownerDocument && root !== document) return [];

      const nodes = [];
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();
      while (node) {
        nodes.push(node);
        node = walker.nextNode();
      }
      return nodes;
    }

    processElement(element, force, processedNodes) {
      if (element.closest('[data-dt-vat-ignore]')) return;

      const identity = this.boundaryIdentity(element);
      const previous = this.boundaryState.get(element);
      const identityChanged = previous && previous.identity !== identity;
      const priceNodes = this.textNodesWithin(element).filter((node) => {
        if (node.parentElement?.closest('[data-dt-vat-ignore]')) return false;
        const tracked = this.textState.get(node);
        return this.hasPoundAmount(node.nodeValue || '') ||
          Boolean(tracked && this.hasPoundAmount(tracked.base));
      });
      const states = [];

      priceNodes.forEach((node, index) => {
        if (processedNodes.has(node)) return;
        processedNodes.add(node);

        if (identityChanged) this.textState.delete(node);

        const current = node.nodeValue || '';
        const priorState = !identityChanged ? previous?.states[index] : null;
        if (!this.textState.has(node) && priorState?.rendered === current) {
          this.textState.set(node, { base: priorState.base, rendered: priorState.rendered });
        }

        this.processTextNode(node, force);
        const state = this.textState.get(node);
        if (state) states.push({ base: state.base, rendered: state.rendered });
      });

      this.boundaryState.set(element, { identity, states });
    }

    processScope(scope, force = false) {
      const processedNodes = new Set();
      const elements = [];
      let normalizedScope = scope;

      if (normalizedScope?.nodeType === Node.TEXT_NODE) {
        normalizedScope = normalizedScope.parentElement;
      }

      if (normalizedScope instanceof Element) {
        normalizedScope = normalizedScope.closest(PRICE_SELECTOR) || normalizedScope;
      }

      if (normalizedScope instanceof Element && normalizedScope.matches(PRICE_SELECTOR)) {
        elements.push(normalizedScope);
      }

      if (normalizedScope?.querySelectorAll) {
        elements.push(...normalizedScope.querySelectorAll(PRICE_SELECTOR));
      }

      const boundaries = [...new Set(elements.map((element) => this.findPriceBoundary(element)))].filter(Boolean);
      boundaries.sort((first, second) => {
        if (first.contains(second)) return 1;
        if (second.contains(first)) return -1;
        return 0;
      });
      boundaries.forEach((boundary) => this.processElement(boundary, force, processedNodes));
    }

    scheduleScope(scope) {
      if (!scope || this.isInsideUtilityBar(scope)) return;

      let normalizedScope = scope.nodeType === Node.TEXT_NODE ? scope.parentElement : scope;
      if (normalizedScope instanceof Element && normalizedScope.closest('[data-dt-vat-ignore]')) return;
      if (normalizedScope instanceof Element) {
        const closestPrice = normalizedScope.closest(PRICE_SELECTOR);
        normalizedScope = this.findPriceBoundary(closestPrice) || closestPrice || normalizedScope;
      }
      if (!normalizedScope || this.isInsideUtilityBar(normalizedScope)) return;

      this.pendingScopes.add(normalizedScope);

      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        const scopes = [...this.pendingScopes];
        this.pendingScopes.clear();
        this.frame = null;
        scopes.forEach((pendingScope) => this.processScope(pendingScope));
      });
    }

    replacementState(node) {
      const state = this.textState.get(node);
      const current = node.nodeValue || '';
      if (!state || state.rendered !== current) return null;

      const priceElement = node.parentElement?.closest(PRICE_SELECTOR);
      const boundary = priceElement ? this.findPriceBoundary(priceElement) : null;
      return {
        base: state.base,
        rendered: state.rendered,
        identity: boundary ? this.boundaryIdentity(boundary) : '',
        role: this.priceRole(node),
      };
    }

    transferReplacementStates(mutations) {
      const retiredByTarget = new Map();

      mutations.forEach((mutation) => {
        if (mutation.type !== 'childList' || this.isInsideUtilityBar(mutation.target)) return;

        const retired = retiredByTarget.get(mutation.target) || [];
        mutation.removedNodes.forEach((removedNode) => {
          this.textNodesWithin(removedNode).forEach((textNode) => {
            const state = this.replacementState(textNode);
            if (state) retired.push(state);
          });
        });
        if (retired.length) retiredByTarget.set(mutation.target, retired);
      });

      mutations.forEach((mutation) => {
        if (mutation.type !== 'childList' || this.isInsideUtilityBar(mutation.target)) return;

        const retired = retiredByTarget.get(mutation.target);
        if (!retired?.length) return;

        mutation.addedNodes.forEach((addedNode) => {
          this.textNodesWithin(addedNode).forEach((textNode) => {
            if (this.textState.has(textNode) || !textNode.parentElement?.closest(PRICE_SELECTOR)) return;

            const current = textNode.nodeValue || '';
            const priceElement = textNode.parentElement.closest(PRICE_SELECTOR);
            const boundary = this.findPriceBoundary(priceElement);
            const identity = boundary ? this.boundaryIdentity(boundary) : '';
            const role = this.priceRole(textNode);
            const matchIndex = retired.findIndex((state) =>
              state.rendered === current &&
              (!state.identity || !identity || state.identity === identity) &&
              (!state.role || !role || state.role === role)
            );

            if (matchIndex === -1) return;
            const [state] = retired.splice(matchIndex, 1);
            this.textState.set(textNode, { base: state.base, rendered: state.rendered });
          });
        });
      });
    }

    observePriceChanges() {
      this.observer = new MutationObserver((mutations) => {
        if (!this.hasUsedExclusive) return;
        this.transferReplacementStates(mutations);

        mutations.forEach((mutation) => {
          if (this.isInsideUtilityBar(mutation.target)) return;

          if (mutation.type === 'characterData') {
            this.scheduleScope(mutation.target.parentElement);
            return;
          }

          this.scheduleScope(mutation.target);
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) this.scheduleScope(node);
          });
        });
      });

      this.observer.observe(document.body, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }

  let sharedVatController = null;

  class DtStoreUtilityBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.timeZone = this.dataset.timeZone || 'Europe/London';
      this.timeFormatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: this.timeZone,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
      this.cutoffs = {
        apc: this.parseCutoff(this.dataset.apcCutoff || '16:45'),
        dpd: this.parseCutoff(this.dataset.dpdCutoff || '15:30'),
        'royal-mail': this.parseCutoff(this.dataset.royalMailCutoff || '16:15'),
      };

      if (!sharedVatController) {
        sharedVatController = new VatPriceController(this);
      } else {
        sharedVatController.registerRoot(this);
      }
      this.vatController = sharedVatController;
      this.updateCutoffs();
      this.clock = window.setInterval(() => this.updateCutoffs(), 1000);

      this.contactPopover = this.querySelector('[data-dt-contact-popover]');
      this.handleDocumentClick = (event) => {
        if (this.contactPopover?.open && !this.contactPopover.contains(event.target)) {
          this.contactPopover.removeAttribute('open');
        }
      };
      this.handleEscape = (event) => {
        if (event.key === 'Escape' && this.contactPopover?.open) {
          this.contactPopover.removeAttribute('open');
          this.contactPopover.querySelector('summary')?.focus();
        }
      };
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleEscape);
    }

    disconnectedCallback() {
      window.clearInterval(this.clock);
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleEscape);
      this.vatController?.unregisterRoot(this);
      this.vatController = null;
      this.dataset.initialized = 'false';
    }

    parseCutoff(value) {
      const [hours, minutes] = String(value).split(':').map(Number);
      if (
        !Number.isInteger(hours) ||
        !Number.isInteger(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return { hours: 0, minutes: 0 };
      }
      return { hours, minutes };
    }

    londonTimeParts(date = new Date()) {
      return this.timeFormatter.formatToParts(date).reduce((parts, part) => {
        if (part.type !== 'literal') parts[part.type] = part.value;
        return parts;
      }, {});
    }

    isWorkingDay(date) {
      const weekday = date.getUTCDay();
      return weekday >= 1 && weekday <= 5;
    }

    zonedDateTimeToEpoch(parts) {
      const wallTime = Date.UTC(
        parts.year,
        parts.month - 1,
        parts.day,
        parts.hours,
        parts.minutes,
        parts.seconds || 0
      );
      let epoch = wallTime;

      // Iterating the formatter offset avoids hard-coding GMT/BST and remains
      // correct across both daylight-saving transitions.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const rendered = this.londonTimeParts(new Date(epoch));
        const renderedWallTime = Date.UTC(
          Number(rendered.year),
          Number(rendered.month) - 1,
          Number(rendered.day),
          Number(rendered.hour),
          Number(rendered.minute),
          Number(rendered.second)
        );
        const adjustment = wallTime - renderedWallTime;
        epoch += adjustment;
        if (adjustment === 0) break;
      }

      return epoch;
    }

    nextWorkingCutoff(now, parts, cutoff) {
      const currentSeconds =
        (Number(parts.hour) * 3600) + (Number(parts.minute) * 60) + Number(parts.second);
      const cutoffSeconds = (cutoff.hours * 3600) + (cutoff.minutes * 60);
      const targetDate = new Date(Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day)
      ));

      if (!this.isWorkingDay(targetDate) || currentSeconds >= cutoffSeconds) {
        targetDate.setUTCDate(targetDate.getUTCDate() + 1);
      }
      while (!this.isWorkingDay(targetDate)) {
        targetDate.setUTCDate(targetDate.getUTCDate() + 1);
      }

      const epoch = this.zonedDateTimeToEpoch({
        year: targetDate.getUTCFullYear(),
        month: targetDate.getUTCMonth() + 1,
        day: targetDate.getUTCDate(),
        hours: cutoff.hours,
        minutes: cutoff.minutes,
        seconds: 0,
      });

      return {
        epoch,
        isToday:
          targetDate.getUTCFullYear() === Number(parts.year) &&
          targetDate.getUTCMonth() + 1 === Number(parts.month) &&
          targetDate.getUTCDate() === Number(parts.day),
        remaining: Math.max(1, Math.ceil((epoch - now.getTime()) / 1000)),
      };
    }

    formatRemaining(totalSeconds) {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
    }

    updateCutoff(carrier, now, parts) {
      const item = this.querySelector(`[data-dt-cutoff-item="${carrier}"]`);
      const output = this.querySelector(`[data-dt-cutoff-countdown="${carrier}"]`);
      const cutoff = this.cutoffs[carrier];
      if (!item || !output || !cutoff) return;

      const labels = {
        apc: 'APC',
        dpd: 'DPD',
        'royal-mail': 'Royal Mail',
      };
      const label = labels[carrier] || carrier;
      const target = this.nextWorkingCutoff(now, parts, cutoff);

      item.dataset.state = target.isToday ? 'open' : 'next-working-day';
      output.textContent = this.formatRemaining(target.remaining);
      output.setAttribute(
        'aria-label',
        target.isToday
          ? `${label} same-day dispatch closes in ${output.textContent}`
          : `${label} next working-day same-day dispatch cutoff is in ${output.textContent}`
      );
    }

    updateCutoffs() {
      const now = new Date();
      const parts = this.londonTimeParts(now);
      this.updateCutoff('apc', now, parts);
      this.updateCutoff('dpd', now, parts);
      this.updateCutoff('royal-mail', now, parts);

      const fridayMessage = this.querySelector('[data-dt-friday-message]');
      if (fridayMessage) fridayMessage.hidden = parts.weekday !== 'Fri';
    }
  }

  if (!customElements.get('dt-store-utility-bar')) {
    customElements.define('dt-store-utility-bar', DtStoreUtilityBar);
  }
})();
